<template>
  <div class="active-filters-wrapper mb-3" style="max-width: calc(70vw - 150px);">
    <!-- Top row with date and practice chips -->
    <div class="d-flex flex-wrap align-center mb-1">
      <v-chip
        v-if="activeFilters.dateRange && activeFilters.dateRange.length === 2"
        small
        color="primary"
        outlined
        class="ma-1 flex-shrink-0"
        @click:close="removeFilter('dateRange')"
      >
        <v-icon small left>mdi-calendar</v-icon>
        {{ formatDateRange(activeFilters.dateRange) }}
      </v-chip>
      <v-chip
        v-if="showPracticeSwitch"
        small
        color="purple"
        outlined
        class="ma-1 flex-shrink-0"
      >
        <v-icon small left>mdi-office-building</v-icon>
        {{ activeFilters.onlyCurrentPractice ? $_t('Current Practice') : $_t('My Referrals') }}
      </v-chip>
    </div>
    
    <!-- Scrollable filter chips row -->
    <div class="d-flex align-center filter-chips-row" v-if="hasActiveFilters">
      <span class="text-caption text--secondary mr-2 flex-shrink-0">{{ $_t('Active filters') }}:</span>
      
      <!-- Horizontal scrollable chips container -->
      <div class="chips-scroll-container flex-grow-1">
        <div class="chips-content d-flex align-center">
          <!-- Date Range Chip -->
        

          <!-- Appointment Statuses Chips -->
          <v-chip
            v-for="status in appointmentStatusChips"
            :key="`status-${status.code}`"
            small
            close
            color="secondary"
            outlined
            class="ma-1 flex-shrink-0"
            @click:close="removeAppointmentStatus(status.code)"
          >
            <v-icon small left>mdi-circle</v-icon>
            {{ status.nameWeb }}
          </v-chip>

          <!-- Speciality Chips -->
          <v-chip
            v-for="speciality in specialityChips"
            :key="`speciality-${speciality.id}`"
            small
            close
            color="accent"
            outlined
            class="ma-1 flex-shrink-0"
            @click:close="removeSpeciality(speciality.id)"
          >
            <v-icon small left>mdi-medical-bag</v-icon>
            {{ speciality.name }}
          </v-chip>

          <!-- Category Chip -->
          <v-chip
            v-if="activeFilters.appointmentCategoryId && categoryName"
            small
            close
            color="info"
            outlined
            class="ma-1 flex-shrink-0"
            @click:close="removeFilter('appointmentCategoryId')"
          >
            <v-icon small left>mdi-account-group</v-icon>
            {{ categoryName }}
          </v-chip>

          <!-- Priority Chip -->
          <v-chip
            v-if="activeFilters.priorityId && priorityName"
            small
            close
            color="warning"
            outlined
            class="ma-1 flex-shrink-0"
            @click:close="removeFilter('priorityId')"
          >
            <v-icon small left>mdi-flag</v-icon>
            {{ priorityName }}
          </v-chip>

          <!-- Clinic Chip -->
          <v-chip
            v-if="activeFilters.clinicId && clinicName"
            small
            close
            color="success"
            outlined
            class="ma-1 flex-shrink-0"
            @click:close="removeFilter('clinicId')"
          >
            <v-icon small left>mdi-hospital-building</v-icon>
            {{ clinicName }}
          </v-chip>

          <!-- Practitioner Chip -->
          <v-chip
            v-if="activeFilters.practitionerId && practitionerName"
            small
            close
            color="error"
            outlined
            class="ma-1 flex-shrink-0"
            @click:close="removeFilter('practitionerId')"
          >
            <v-icon small left>mdi-doctor</v-icon>
            {{ practitionerName }}
          </v-chip>
          <!-- Patient Location Chips -->
          <v-chip
            v-for="location in patientLocationChips"
            :key="`location-${location.id}`"
            small
            close
            color="teal"
            outlined
            class="ma-1 flex-shrink-0"
            @click:close="removePatientLocation(location.id)"
          >
            <v-icon small left>mdi-map-marker</v-icon>
            {{ location.name }}
          </v-chip>

          <!-- Alert Status Chips -->
          <v-chip
            v-for="status in alertStatusChips"
            :key="`alert-status-${status.code}`"
            small
            close
            color="orange"
            outlined
            class="ma-1 flex-shrink-0"
            @click:close="removeAlertStatus(status.code)"
          >
            <v-icon small left>mdi-alert-circle</v-icon>
            {{ status.name }}
          </v-chip>

          <!-- Alert Type Chips -->
          <v-chip
            v-for="type in alertTypeChips"
            :key="`alert-type-${type.code}`"
            small
            close
            color="deep-orange"
            outlined
            class="ma-1 flex-shrink-0"
            @click:close="removeAlertType(type.code)"
          >
            <v-icon small left>mdi-alert</v-icon>
            {{ type.name }}
          </v-chip>
        </div>
      </div>

      <!-- Clear All Button -->
      <v-btn
        v-if="hasActiveFilters"
        x-small
        text
        color="error"
        class="ml-2 flex-shrink-0"
        @click="clearAllFilters"
      >
        <v-icon small left>mdi-close-circle</v-icon>
        {{ $_t('Clear All') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import useDateHelpers from '@helpers/useDateHelpers'

const { useHumanDate } = useDateHelpers()

export default {
  name: 'ActiveFilterChips',
  props: {
    activeFilters: {
      type: Object,
      required: true
    },
    filterData: {
      type: Object,
      default: () => ({
        appointmentStatuses: []
      })
    },
    specialitiesList: {
      type: Array,
      default: () => []
    },
    prioritiesList: {
      type: Array,
      default: () => []
    },
    clinicsList: {
      type: Array,
      default: () => []
    },
    practitionersList: {
      type: Array,
      default: () => []
    },
    categoriesList: {
      type: Array,
      default: () => []
    },
    patientLocationsList: {
      type: Array,
      default: () => []
    },
    alertStatusesList: {
      type: Array,
      default: () => []
    },
    alertTypesList: {
      type: Array,
      default: () => []
    },
    showPracticeSwitch: {
      type: Boolean,
      default: false
    },
    defaultFilters: {
      type: Object,
      default: () => ({
        dateRange: null,
        appointmentStatuses: [],
        onlyCurrentPractice: true,
        appointmentCategoryId: null,
        specialityIds: null,
        priorityId: null,
        clinicId: null,
        practitionerId: null
      })
    }
  },
  computed: {
    hasActiveFilters() {
      return this.appointmentStatusChips.length > 0 ||
             this.specialityChips.length > 0 ||
             this.activeFilters.appointmentCategoryId ||
             this.activeFilters.priorityId ||
             this.activeFilters.clinicId ||
             this.activeFilters.practitionerId ||
             this.patientLocationChips.length > 0 ||
             this.alertStatusChips.length > 0 ||
             this.alertTypeChips.length > 0
    },
    
    appointmentStatusChips() {
      if (!this.activeFilters.appointmentStatuses || this.activeFilters.appointmentStatuses.length === 0) {
        return []
      }
      return this.filterData.appointmentStatuses.filter(status => 
        this.activeFilters.appointmentStatuses.includes(status.code)
      )
    },
    
    specialityChips() {
      if (!this.activeFilters.specialityIds || this.activeFilters.specialityIds.length === 0) {
        return []
      }
      return this.specialitiesList.filter(speciality => 
        this.activeFilters.specialityIds.includes(speciality.id)
      )
    },
    
    categoryName() {
      if (!this.activeFilters.appointmentCategoryId) {
        return null
      }
      const category = this.categoriesList.find(c => c.id === this.activeFilters.appointmentCategoryId)
      return category ? category.name : null
    },
    
    priorityName() {
      if (!this.activeFilters.priorityId) {
      return null
      }
      const priority = this.prioritiesList.find(p => p.id === this.activeFilters.priorityId)
      return priority ? priority.name : null
    },
    
    clinicName() {
      if (!this.activeFilters.clinicId) {
        return null
      }
      const clinic = this.clinicsList.find(c => c.id === this.activeFilters.clinicId)
      return clinic ? clinic.name : null
    },
    
    practitionerName() {
      if (!this.activeFilters.practitionerId) {
        return null
      }
      const practitioner = this.practitionersList.find(p => p.id === this.activeFilters.practitionerId)
      return practitioner ? practitioner.fullName : null
    },    
    patientLocationChips() {
      if (!this.activeFilters.patientLocationIds || this.activeFilters.patientLocationIds.length === 0) {
        return []
      }
      return this.patientLocationsList.filter(location => 
        this.activeFilters.patientLocationIds.includes(location.id)
      )
    },
    
    alertStatusChips() {
      if (!this.activeFilters.alertStatuses || this.activeFilters.alertStatuses.length === 0) {
        return []
      }
      return this.alertStatusesList.filter(status => 
        this.activeFilters.alertStatuses.includes(status.code)
      )
    },
    
    alertTypeChips() {
      if (!this.activeFilters.alertTypes || this.activeFilters.alertTypes.length === 0) {
        return []
      }
      return this.alertTypesList.filter(type => 
        this.activeFilters.alertTypes.includes(type.code)
      )
    }
  },
  methods: {
    formatDateRange(dateRange) {
      if (!dateRange || dateRange.length !== 2){
        return ''
      } 
      if (dateRange[0] === dateRange[1]) {
        return useHumanDate(dateRange[0])
      }
      return `${useHumanDate(dateRange[0])} - ${useHumanDate(dateRange[1])}`
    },
    
    removeFilter(filterKey) {
      this.$emit('remove-filter', filterKey)
    },
    
    removeAppointmentStatus(statusCode) {
      const updatedStatuses = this.activeFilters.appointmentStatuses.filter(code => code !== statusCode)
      this.$emit('update-filter', 'appointmentStatuses', updatedStatuses)
    },
    
    removeSpeciality(specialityId) {
      const updatedSpecialities = this.activeFilters.specialityIds.filter(id => id !== specialityId)
      this.$emit('update-filter', 'specialityIds', updatedSpecialities)
    },
    
    togglePracticeSwitch() {
      this.$emit('update-filter', 'onlyCurrentPractice', this.defaultFilters.onlyCurrentPractice)
    },
    
    removePatientLocation(locationId) {
      const updatedLocations = this.activeFilters.patientLocationIds.filter(id => id !== locationId)
      this.$emit('update-filter', 'patientLocationIds', updatedLocations)
    },
    
    removeAlertStatus(statusCode) {
      const updatedStatuses = this.activeFilters.alertStatuses.filter(code => code !== statusCode)
      this.$emit('update-filter', 'alertStatuses', updatedStatuses)
    },
    
    removeAlertType(typeCode) {
      const updatedTypes = this.activeFilters.alertTypes.filter(code => code !== typeCode)
      this.$emit('update-filter', 'alertTypes', updatedTypes)
    },
    
    clearAllFilters() {
      this.$emit('clear-all-filters')
    }
  }
}
</script>

<style scoped>
.active-filters-wrapper {
  max-width: 100%;
  width: 100%;
}

.filter-chips-row {
  max-width: 100%;
  min-width: 0; /* Important: allows flex children to shrink below content size */
}

.chips-scroll-container {
  overflow-x: auto;
  overflow-y: hidden;
  max-width: 100%;
  min-width: 0; /* Important: allows flex children to shrink below content size */
  /* Hide scrollbar for webkit browsers */
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.chips-scroll-container::-webkit-scrollbar {
  height: 4px;
}

.chips-scroll-container::-webkit-scrollbar-track {
  background: transparent;
}

.chips-scroll-container::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
}

.chips-scroll-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.3);
}

.chips-content {
  /* Prevent wrapping and enable horizontal scroll */
  flex-wrap: nowrap;
  min-width: min-content;
  padding: 2px 0;
}

/* Ensure chips don't shrink */
.chips-content .v-chip {
  white-space: nowrap;
}

/* Smooth scrolling */
.chips-scroll-container {
  scroll-behavior: smooth;
}

/* Add some visual indication when scrollable */
.chips-scroll-container::after {
  content: '';
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 20px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.chips-scroll-container:hover::after {
  opacity: 1;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .active-filters-container {
    padding: 6px 8px;
  }
  
  .chips-scroll-container::-webkit-scrollbar {
    height: 6px;
  }
}
</style>
